import { useEffect, useMemo, useState } from 'react'
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    Controls,
    Handle,
    Position,
    useReactFlow,
    useNodesInitialized,
    type Node,
    type Edge,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useTranslation } from 'react-i18next'
import { Network } from 'lucide-react'
import { getLayoutedElements } from '@/features/visualization/_legacy/utils/elk'

export interface IaCFileLike { filename: string; code: string }

type Kind = 'root' | 'module' | 'resource'
interface RawNode { id: string; label: string; kind: Kind }
interface RawEdge { source: string; target: string }

const MAX_RESOURCES = 160

// Friendly names for common Terraform resource types (cloud-native target environment).
const RESOURCE_NAMES: Record<string, string> = {
    aws_vpc: 'VPC', aws_subnet: 'Subnet', aws_instance: 'EC2 Instance', aws_db_instance: 'RDS Database',
    aws_security_group: 'Security Group', aws_kms_key: 'KMS Key', aws_s3_bucket: 'S3 Bucket', aws_iam_role: 'IAM Role',
    aws_iam_policy: 'IAM Policy', aws_nat_gateway: 'NAT Gateway', aws_internet_gateway: 'Internet Gateway',
    aws_route_table: 'Route Table', aws_lb: 'Load Balancer', aws_cloudwatch_metric_alarm: 'CloudWatch Alarm',
    aws_flow_log: 'Flow Log', aws_guardduty_detector: 'GuardDuty', aws_cloudtrail: 'CloudTrail',
    aws_kms_alias: 'KMS Alias', aws_dynamodb_table: 'DynamoDB Table', aws_ssm_parameter: 'SSM Parameter',
    azurerm_virtual_network: 'VNet', azurerm_subnet: 'Subnet', azurerm_windows_virtual_machine: 'Windows VM',
    azurerm_linux_virtual_machine: 'Linux VM', azurerm_mssql_database: 'SQL Database', azurerm_mssql_server: 'SQL Server',
    azurerm_key_vault: 'Key Vault', azurerm_network_security_group: 'NSG', azurerm_storage_account: 'Storage Account',
    azurerm_resource_group: 'Resource Group', azurerm_bastion_host: 'Bastion',
    google_compute_network: 'VPC Network', google_compute_subnetwork: 'Subnet', google_compute_instance: 'Compute Instance',
    google_sql_database_instance: 'Cloud SQL', google_kms_crypto_key: 'KMS Key', google_storage_bucket: 'Storage Bucket',
}

function friendlyType(type: string): string {
    return RESOURCE_NAMES[type] || type.replace(/^(aws|azurerm|google|azuread|random|tls)_/, '').replace(/_/g, ' ')
}

function moduleOf(path: string): string {
    const m = path.match(/modules\/([^/]+)\//i)
    if (m) return m[1]
    if (/^|\//.test(path) && !path.includes('/')) return 'root'
    return 'root'
}

/** Build a Root → Module → Resource graph from the project's Terraform files. */
function parseTerraform(files: IaCFileLike[]): { nodes: RawNode[]; edges: RawEdge[] } {
    const byModule = new Map<string, { type: string; name: string }[]>()
    for (const f of files) {
        if (!/\.tf$/i.test(f.filename)) continue
        const mod = moduleOf(f.filename)
        const re = /resource\s+"([^"]+)"\s+"([^"]+)"/g
        let m: RegExpExecArray | null
        while ((m = re.exec(f.code || ''))) {
            if (!byModule.has(mod)) byModule.set(mod, [])
            byModule.get(mod)!.push({ type: m[1], name: m[2] })
        }
    }
    const nodes: RawNode[] = []
    const edges: RawEdge[] = []
    const rootId = 'root'
    let total = 0
    const modules = [...byModule.keys()].sort((a) => (a === 'root' ? -1 : 0))
    if (modules.length === 0) return { nodes: [], edges: [] }
    nodes.push({ id: rootId, label: 'Target Environment', kind: 'root' })
    let mi = 0
    for (const mod of modules) {
        const modId = `m_${mi++}`
        nodes.push({ id: modId, label: mod === 'root' ? 'Root' : mod, kind: 'module' })
        edges.push({ source: rootId, target: modId })
        const resources = byModule.get(mod) || []
        // de-dupe by type+name and cap to keep readable
        const seen = new Set<string>()
        for (const r of resources) {
            if (total >= MAX_RESOURCES) break
            const key = `${r.type}.${r.name}`
            if (seen.has(key)) continue
            seen.add(key)
            const rid = `r_${total++}`
            nodes.push({ id: rid, label: `${friendlyType(r.type)} · ${r.name}`, kind: 'resource' })
            edges.push({ source: modId, target: rid })
        }
    }
    return { nodes, edges }
}

/** Fallback: outline a markdown doc by heading hierarchy + bullets (no terraform). */
function parseOutline(md: string): { nodes: RawNode[]; edges: RawEdge[] } {
    const nodes: RawNode[] = []
    const edges: RawEdge[] = []
    let counter = 0
    const newId = () => `n${counter++}`
    const stack: { level: number; id: string }[] = []
    let lastHeadingId: string | null = null
    let inFence = false
    const clean = (s: string) => s.replace(/`/g, '').replace(/[*_]{1,3}/g, '').replace(/\[(.*?)\]\(.*?\)/g, '$1').trim()
    for (const raw of (md || '').split('\n')) {
        if (nodes.length >= MAX_RESOURCES) break
        if (/^```/.test(raw.trim())) { inFence = !inFence; continue }
        if (inFence) continue
        const hm = raw.match(/^(#{1,4})\s+(.*)/)
        if (hm) {
            const level = hm[1].length
            const label = clean(hm[2]); if (!label) continue
            const id = newId()
            while (stack.length && stack[stack.length - 1].level >= level) stack.pop()
            const parent = stack.length ? stack[stack.length - 1].id : null
            nodes.push({ id, label, kind: level === 1 ? 'root' : 'module' })
            if (parent) edges.push({ source: parent, target: id })
            stack.push({ level, id }); lastHeadingId = id
            continue
        }
        const lm = raw.match(/^\s*[-*+]\s+(.*)/)
        if (lm && lastHeadingId) {
            const label = clean(lm[1]); if (!label || label.length > 80) continue
            const id = newId()
            nodes.push({ id, label, kind: 'resource' })
            edges.push({ source: lastHeadingId, target: id })
        }
    }
    return { nodes, edges }
}

function IaCNode({ data }: { data: { label: string; kind: Kind } }) {
    const cls =
        data.kind === 'root' ? 'bg-primary text-primary-foreground border-primary'
            : data.kind === 'module' ? 'bg-card text-foreground border-primary/40 font-semibold'
            : 'bg-muted/50 text-muted-foreground border-border'
    return (
        <div className={`rounded-md border px-3 py-2 text-xs shadow-sm max-w-[210px] truncate ${cls}`} title={data.label}>
            <Handle type="target" position={Position.Top} className="!h-1.5 !w-1.5 !bg-muted-foreground" />
            {data.label}
            <Handle type="source" position={Position.Bottom} className="!h-1.5 !w-1.5 !bg-muted-foreground" />
        </div>
    )
}
const nodeTypes = { iac: IaCNode }

/** Re-fits the view once nodes are measured (async ELK layout). */
function FitOnReady({ signature }: { signature: number }) {
    const initialized = useNodesInitialized()
    const { fitView } = useReactFlow()
    useEffect(() => {
        if (initialized) fitView({ padding: 0.15, duration: 300 })
    }, [initialized, signature, fitView])
    return null
}

/**
 * Visualizes the IaC target environment with the app's ELK layout + React Flow.
 * Primary source is the Terraform files (modules → resources); falls back to the
 * markdown guide outline when no .tf files are present.
 */
export function IaCOverviewFlow({ files, markdown }: { files: IaCFileLike[]; markdown?: string }) {
    const { t } = useTranslation()
    const [graph, setGraph] = useState<{ nodes: Node[]; edges: Edge[] } | null>(null)

    const tfFiles = useMemo(() => (files || []).filter((f) => /\.tf$/i.test(f.filename)), [files])
    const parsed = useMemo(() => {
        if (tfFiles.length > 0) return parseTerraform(tfFiles)
        return parseOutline(markdown || '')
    }, [tfFiles, markdown])

    useEffect(() => {
        let cancelled = false
        if (!parsed.nodes.length) { setGraph({ nodes: [], edges: [] }); return }
        const rfNodes: Node[] = parsed.nodes.map((n) => ({ id: n.id, position: { x: 0, y: 0 }, data: { label: n.label, kind: n.kind }, type: 'iac' }))
        const rfEdges: Edge[] = parsed.edges.map((e, i) => ({ id: `e${i}`, source: e.source, target: e.target }))
        getLayoutedElements(rfNodes, rfEdges, 'TB', 1.5, 1.3, 'none', 220, 56)
            .then(({ nodes, edges }: any) => {
                if (cancelled) return
                setGraph({
                    nodes: nodes.map((n: any) => ({ ...n, type: 'iac' })),
                    edges: edges.map((e: any) => ({ ...e, type: 'smoothstep', style: { stroke: '#94a3b8', strokeWidth: 1.5 } })),
                })
            })
            .catch(() => { if (!cancelled) setGraph({ nodes: rfNodes, edges: rfEdges }) })
        return () => { cancelled = true }
    }, [parsed])

    const isEmpty = (files?.length ?? 0) === 0 && !(markdown && markdown.trim())
    if (isEmpty) {
        return (
            <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
                <div className="text-center">
                    <Network className="mx-auto mb-2 h-10 w-10 opacity-30" />
                    <p>{t('iac.overviewEmpty', 'Generate infrastructure to see the target-environment overview.')}</p>
                </div>
            </div>
        )
    }
    if (!graph) {
        return <div className="flex h-full items-center justify-center text-muted-foreground text-xs">{t('common.loading', 'Loading…')}</div>
    }

    return (
        <div className="h-full w-full">
            <ReactFlowProvider>
                <ReactFlow
                    nodes={graph.nodes}
                    edges={graph.edges}
                    nodeTypes={nodeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.15 }}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    proOptions={{ hideAttribution: true }}
                    minZoom={0.05}
                >
                    <FitOnReady signature={graph.nodes.length} />
                    <Background />
                    <Controls showInteractive={false} />
                </ReactFlow>
            </ReactFlowProvider>
        </div>
    )
}
